// ==========================
// Version 7 — EditMemoryModal.js
// - Adds aria-labelledby + aria-describedby
// - Adds focus trap + restores focus to opener on close
// - Esc/backdrop close preserved
// ==========================
import React from 'react';
import { uploadFile } from '../db';
import { useAuth } from '../context/AuthContext';
import { toast } from '../utils/toast';

const NAVY = '#0A1A33';
const GOLD = '#C9A44C';
const IVORY = '#FDFCF9';
const WARM_GRAY = '#888888';

export default function EditMemoryModal({ open, memory, onClose, onSave }) {
  const { currentUser } = useAuth();

  const [date, setDate] = React.useState('');
  const [time, setTime] = React.useState('');
  const [story, setStory] = React.useState('');
  const [isPublic, setIsPublic] = React.useState(false);
  const [isMediaPublic, setIsMediaPublic] = React.useState(false);
  const [imageUrls, setImageUrls] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);

  // a11y: ids and refs for focus mgmt
  const titleId = React.useId();
  const descId = React.useId();
  const firstInteractiveRef = React.useRef(null);
  const previouslyFocusedRef = React.useRef(null);

  // seed local state
  React.useEffect(() => {
    if (!open || !memory) return;
    setDate(memory.date || '');
    setTime(memory.time || '');
    setStory(memory.story || '');
    setIsPublic(!!memory.isPublic);
    setIsMediaPublic(!!memory.isMediaPublic);
    setImageUrls(Array.isArray(memory.imageUrls) ? [...memory.imageUrls] : []);
  }, [open, memory]);

  // focus trap + restore focus to opener
  React.useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current = document.activeElement;
    const container = document.getElementById(`editmem-${titleId}`)?.closest('[role="dialog"]') || null;

    // focus first interactive
    setTimeout(() => firstInteractiveRef.current?.focus(), 0);

    const handleKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key !== 'Tab') return;

      // trap
      const focusEls = container
        ? Array.from(container.querySelectorAll(
            'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])'
          ))
        : [];
      if (!focusEls.length) return;
      const first = focusEls[0];
      const last = focusEls[focusEls.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      // restore focus
      try { previouslyFocusedRef.current?.focus?.(); } catch {}
    };
  }, [open, onClose, titleId]);

  if (!open || !memory) return null;

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!currentUser) { toast.error('Please login'); return; }

    try {
      setUploading(true);
      const urls = [];
      for (const f of files) {
        const url = await uploadFile(f, currentUser.uid, {
          mediaPublic: isMediaPublic,
        });
        urls.push(url);
      }
      setImageUrls((prev) => [...prev, ...urls]);
      toast.success('Photos uploaded.');
    } catch (err) {
      console.error(err);
      toast.error('Upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (idx) => setImageUrls((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = () => {
    const updates = {
      date,
      time: time || null,
      story,
      isPublic,
      isMediaPublic,
      imageUrls: (imageUrls || []).filter((u) => typeof u === 'string' && u.trim().length > 0),
    };
    onSave?.(updates);
  };

  const stop = (e) => e.stopPropagation();

  return (
    <div
      style={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      onClick={onClose}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div style={styles.modal} onClick={stop} onMouseDown={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <h3 id={titleId} style={styles.title}>Edit Memory</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={styles.closeBtn}
          >
            ✕
          </button>
        </div>

        {/* Body (scrollable) */}
        <div id={descId} style={styles.body}>
          <label style={styles.label} htmlFor="mem-date">Date</label>
          <input
            id="mem-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={styles.input}
            ref={firstInteractiveRef}
          />

          <label style={styles.label} htmlFor="mem-time">Time (optional)</label>
          <input
            id="mem-time"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            style={styles.input}
          />

          <label style={styles.label} htmlFor="mem-story">Story</label>
          <textarea
            id="mem-story"
            value={story}
            onChange={(e) => setStory(e.target.value)}
            style={styles.textarea}
            placeholder="Update your memory…"
          />

          <label style={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            <span>Public</span>
          </label>

          <label style={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={isMediaPublic}
              onChange={(e) => setIsMediaPublic(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            <span>
              Media public <span style={styles.hint}>(visible to logged-in users)</span>
            </span>
          </label>

          <div style={{ marginTop: 6 }}>
            <label style={styles.label}>Photos</label>
            <input type="file" accept="image/*" multiple onChange={onPickFiles} style={styles.file} />

            {imageUrls.length > 0 && (
              <div style={styles.thumbGrid}>
                {imageUrls.map((u, i) => (
                  <div key={i} style={styles.thumbWrap}>
                    <img src={u} alt="" style={styles.thumb} />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      style={styles.thumbRemove}
                      disabled={uploading}
                      title="Remove photo"
                      aria-label="Remove photo"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button onClick={onClose} style={styles.btnGhost} disabled={uploading}>
            Cancel
          </button>
          <button onClick={handleSave} style={styles.btnPrimary} disabled={uploading}>
            {uploading ? 'Uploading…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(10,26,51,0.55)',
    display: 'grid',
    placeItems: 'center',
    zIndex: 1000,
    padding: 12,
  },
  modal: {
    width: 'min(92vw, 600px)',
    maxHeight: '86vh',
    background: IVORY,
    color: NAVY,
    borderRadius: 16,
    border: `1px solid rgba(201,164,76,0.35)`,
    boxShadow: '0 28px 72px rgba(10,26,51,0.50)',
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px 10px',
    borderBottom: '1px solid rgba(10,26,51,0.08)',
  },
  title: { margin: 0, fontFamily: 'serif', fontSize: 20 },
  closeBtn: {
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    color: NAVY,
    fontSize: 18,
    lineHeight: 1,
    cursor: 'pointer',
    borderRadius: 8,
    padding: 6,
  },
  body: { padding: 16, overflow: 'auto' },
  label: { display: 'block', fontWeight: 800, margin: '8px 0 6px' },
  input: {
    width: '100%',
    padding: 12,
    borderRadius: 12,
    border: `1px solid rgba(10,26,51,0.15)`,
    background: '#fff',
    color: NAVY,
    outline: 'none',
    fontSize: 14,
  },
  textarea: {
    width: '100%',
    minHeight: 120,
    padding: 12,
    borderRadius: 12,
    border: `1px solid rgba(10,26,51,0.15)`,
    background: '#fff',
    color: NAVY,
    outline: 'none',
    fontSize: 14,
    lineHeight: 1.5,
    resize: 'vertical',
  },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontWeight: 700 },
  hint: { color: WARM_GRAY, fontWeight: 600 },
  file: {
    display: 'block',
    marginTop: 4,
    padding: 8,
    borderRadius: 12,
    border: `1px solid rgba(10,26,51,0.15)`,
    background: '#fff',
    color: NAVY,
  },
  thumbGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))', gap: 10, marginTop: 10 },
  thumbWrap: { position: 'relative', borderRadius: 10, overflow: 'hidden', border: `1px solid rgba(201,164,76,0.35)` },
  thumb: { width: '100%', height: 84, objectFit: 'cover', display: 'block', background: '#fff' },
  thumbRemove: {
    position: 'absolute',
    top: 6, right: 6,
    border: 'none',
    background: GOLD,
    color: NAVY,
    borderRadius: 999,
    width: 22, height: 22,
    cursor: 'pointer',
    fontSize: 12,
    lineHeight: '22px',
    boxShadow: '0 4px 10px rgba(10,26,51,0.25)',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    padding: 12,
    borderTop: '1px solid rgba(10,26,51,0.08)',
  },
  btnGhost: {
    padding: '10px 14px',
    borderRadius: 12,
    border: `1px solid rgba(10,26,51,0.25)`,
    background: 'transparent',
    color: NAVY,
    cursor: 'pointer',
    fontWeight: 800,
  },
  btnPrimary: {
    padding: '10px 14px',
    borderRadius: 12,
    border: 'none',
    background: GOLD,
    color: NAVY,
    cursor: 'pointer',
    fontWeight: 900,
  },
};
// ==========================
// End Version 7 — EditMemoryModal.js
// ==========================
